import React, { useState, useEffect, useCallback } from 'react';
import BuyerPortalLayout from '../../components/buyer/BuyerPortalLayout';
import { fetchBuyerDeliveries } from '../../lib/buyerApi';

const BuyerDeliveriesPage: React.FC = () => {
  const [shipments, setShipments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchBuyerDeliveries();
      setShipments(res.items || res.shipments || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const active = shipments.filter(s => ['created', 'picked_up', 'in_transit', 'partially_delivered'].includes(s.status));
  const completed = shipments.filter(s => ['delivered', 'confirmed'].includes(s.status));

  return (
    <BuyerPortalLayout title="Deliveries">
      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : shipments.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <p className="text-5xl mb-4">🚚</p>
          <h3 className="text-lg font-bold text-gray-900 mb-2">No deliveries yet</h3>
          <p className="text-gray-500 text-sm">Shipments will appear here once orders are dispatched.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {active.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">In Transit ({active.length})</h3>
              <div className="space-y-3">
                {active.map((s: any) => (
                  <div key={s.id} className="bg-white border border-gray-100 rounded-2xl p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-bold text-gray-900">{s.shipment_number || `SHP-${s.id}`}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{s.carrier_name || 'Carrier TBD'}</p>
                        {s.supplier_order_number && <p className="text-xs text-gray-400">Order: {s.supplier_order_number}</p>}
                      </div>
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                        s.status === 'in_transit' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                      }`}>{(s.status || '').replace(/_/g, ' ')}</span>
                    </div>
                    {s.tracking_url && (
                      <a href={s.tracking_url} target="_blank" rel="noopener noreferrer" className="mt-2 text-xs text-blue-600 font-bold hover:underline">
                        Track shipment →
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {completed.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Completed ({completed.length})</h3>
              <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100"><tr>
                    <th className="px-5 py-2.5 text-left font-bold text-gray-500 text-xs uppercase">Shipment</th>
                    <th className="px-5 py-2.5 text-left font-bold text-gray-500 text-xs uppercase">Carrier</th>
                    <th className="px-5 py-2.5 text-left font-bold text-gray-500 text-xs uppercase">Status</th>
                    <th className="px-5 py-2.5 text-left font-bold text-gray-500 text-xs uppercase">Date</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {completed.map((s: any) => (
                      <tr key={s.id}>
                        <td className="px-5 py-3 font-mono text-xs">{s.shipment_number || `SHP-${s.id}`}</td>
                        <td className="px-5 py-3 text-gray-500 text-xs">{s.carrier_name || '—'}</td>
                        <td className="px-5 py-3"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">{s.status}</span></td>
                        <td className="px-5 py-3 text-gray-500 text-xs">{s.delivered_at ? new Date(s.delivered_at).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </BuyerPortalLayout>
  );
};

export default BuyerDeliveriesPage;
